import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FiDownload, FiChevronDown, FiChevronUp, FiSearch, FiDatabase, FiArrowRight, FiBarChart2, FiUsers, FiBriefcase, FiDollarSign, FiCalendar, FiMapPin, FiTrendingUp } from 'react-icons/fi';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from 'recharts';
import { api } from '../api';
import { formatCurrency, formatDate, formatYear } from '../utils/formatters';
import { useResearchAgent } from '../context/ResearchAgentContext';
import './DataExplorer.css';

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Custom pie chart label renderer - only show percentage, positioned better
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null; // Don't show labels for tiny slices
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

function DataExplorer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updatePageContext, clearPageContext } = useResearchAgent();

  // Search export data
  const [searchExport, setSearchExport] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [investors, setInvestors] = useState([]);

  // Database-wide stats for comparison
  const [dbStats, setDbStats] = useState(null);

  // Section collapse state
  const [collapsedSections, setCollapsedSections] = useState({
    companies: false,
    deals: false,
    investors: false
  });

  // Expanded default columns for richer data view
  const defaultColumns = {
    companies: ['name', 'primary_industry_code', 'hq_location', 'employees', 'total_raised', 'revenue', 'year_founded', 'company_financing_status', 'business_status', 'keywords'],
    deals: ['deal_date', 'companies', 'deal_type', 'deal_type_2', 'deal_size_usd', 'post_valuation', 'leadsole_investors', 'deal_status', 'financing_status'],
    investors: ['name', 'investor_type', 'hq_location', 'hq_country', 'aum', 'dry_powder', 'investments_count', 'total_investments', 'total_exits', 'investor_status', 'website']
  };

  // Load search export on mount
  useEffect(() => {
    const source = searchParams.get('source');

    if (source === 'search') {
      const storedExport = localStorage.getItem('transcend_search_export');
      if (storedExport) {
        try {
          const exportData = JSON.parse(storedExport);
          console.log('📊 Loading Search Export:', exportData);

          // Check if data is recent (within last 30 minutes)
          const timestamp = new Date(exportData.timestamp);
          const now = new Date();
          const ageMinutes = (now - timestamp) / 1000 / 60;

          if (ageMinutes < 30) {
            setSearchExport(exportData);
            setCompanies(exportData.results?.companies || []);
            setDeals(exportData.results?.deals || []);
            setInvestors(exportData.results?.investors || []);

            // Clear the localStorage after loading
            localStorage.removeItem('transcend_search_export');
          } else {
            console.log('⚠️ Search export expired (older than 30 minutes)');
            localStorage.removeItem('transcend_search_export');
          }
        } catch (e) {
          console.error('Error parsing search export:', e);
          localStorage.removeItem('transcend_search_export');
        }
      }
    }
  }, [searchParams]);

  // Fetch database-wide stats for comparison
  useEffect(() => {
    const fetchDbStats = async () => {
      try {
        const response = await api.getStats();
        if (response.data) {
          setDbStats(response.data);
        }
      } catch (e) {
        console.error('Error fetching database stats:', e);
      }
    };
    fetchDbStats();
  }, []);

  // Update Research Agent page context when in Data Explorer
  useEffect(() => {
    const totalResults = companies.length + deals.length + investors.length;
    const activeFilters = [];

    // Build active filters from search export
    if (searchExport?.query) {
      activeFilters.push({ field: 'query', value: searchExport.query });
    }
    if (searchExport?.entityTypes) {
      activeFilters.push({ field: 'entityTypes', value: searchExport.entityTypes.join(', ') });
    }

    updatePageContext('data-explorer', null, {
      resultCount: totalResults,
      entityType: searchExport?.entityTypes?.join(', ') || 'all',
      activeFilters,
      companiesCount: companies.length,
      dealsCount: deals.length,
      investorsCount: investors.length,
      hasSearchExport: !!searchExport
    });

    // Clear context when leaving the page
    return () => clearPageContext();
  }, [companies, deals, investors, searchExport, updatePageContext, clearPageContext]);

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to ensure URL has protocol
  const formatWebsiteUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  // Export to CSV
  const exportToCSV = (entityType, data) => {
    if (!data || data.length === 0) return;

    const columns = defaultColumns[entityType];
    const headers = columns.map(col => col.replace(/_/g, ' ').toUpperCase());

    const rows = data.map(item => {
      return columns.map(column => {
        const value = item[column];
        if (value === null || value === undefined) return '';
        if (Array.isArray(value)) return value.join('; ');
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value).replace(/"/g, '""');
      });
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    a.download = `transcend_${entityType}_${data.length}records_${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate summary stats for companies
  const companyStats = useMemo(() => {
    if (companies.length === 0) return null;

    const employees = companies.map(c => parseFloat(c.employees)).filter(e => !isNaN(e) && e > 0);
    const totalRaised = companies.map(c => parseFloat(c.total_raised_usd || c.total_raised)).filter(t => !isNaN(t) && t > 0);
    const years = companies.map(c => parseInt(c.year_founded)).filter(y => !isNaN(y) && y > 1900);

    // Top industries - truncate names more aggressively
    const industryCounts = {};
    companies.forEach(c => {
      const ind = c.primary_industry_code || c.primary_industry || 'Unknown';
      industryCounts[ind] = (industryCounts[ind] || 0) + 1;
    });
    const topIndustries = Object.entries(industryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name: name.length > 15 ? name.slice(0, 15) + '...' : name, fullName: name, value }));

    // Top locations - truncate names
    const locationCounts = {};
    companies.forEach(c => {
      const loc = c.hq_location || c.hq_city || 'Unknown';
      locationCounts[loc] = (locationCounts[loc] || 0) + 1;
    });
    const topLocations = Object.entries(locationCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name: name.length > 15 ? name.slice(0, 15) + '...' : name, fullName: name, value }));

    // Financing status distribution - simplify names
    const financingStatusCounts = {};
    companies.forEach(c => {
      let status = c.company_financing_status || c.financing_status || 'Unknown';
      // Shorten common status names
      if (status.length > 20) status = status.slice(0, 18) + '..';
      financingStatusCounts[status] = (financingStatusCounts[status] || 0) + 1;
    });
    const financingStatusData = Object.entries(financingStatusCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    // Founded by year
    const yearCounts = {};
    years.forEach(y => {
      yearCounts[y] = (yearCounts[y] || 0) + 1;
    });
    const foundedByYear = Object.entries(yearCounts)
      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
      .slice(-10)
      .map(([year, count]) => ({ year, count }));

    return {
      count: companies.length,
      avgEmployees: employees.length > 0 ? Math.round(employees.reduce((a, b) => a + b, 0) / employees.length) : 0,
      medianEmployees: employees.length > 0 ? employees.sort((a, b) => a - b)[Math.floor(employees.length / 2)] : 0,
      totalRaisedSum: totalRaised.reduce((a, b) => a + b, 0),
      avgTotalRaised: totalRaised.length > 0 ? totalRaised.reduce((a, b) => a + b, 0) / totalRaised.length : 0,
      yearRange: years.length > 0 ? `${Math.min(...years)} - ${Math.max(...years)}` : 'N/A',
      topIndustries,
      topLocations,
      financingStatusData,
      foundedByYear
    };
  }, [companies]);

  // Calculate summary stats for deals
  const dealStats = useMemo(() => {
    if (deals.length === 0) return null;

    const dealSizes = deals.map(d => parseFloat(d.deal_size_usd)).filter(s => !isNaN(s) && s > 0);
    const dates = deals.map(d => d.deal_date).filter(d => d);

    // Top deal types - truncate
    const dealTypeCounts = {};
    deals.forEach(d => {
      const type = d.deal_type || 'Unknown';
      dealTypeCounts[type] = (dealTypeCounts[type] || 0) + 1;
    });
    const topDealTypes = Object.entries(dealTypeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, value]) => ({ name: name.length > 12 ? name.slice(0, 12) + '..' : name, fullName: name, value }));

    // Top lead investors - truncate
    const investorCounts = {};
    deals.forEach(d => {
      const investors = d.leadsole_investors || d.lead_investors;
      if (investors) {
        const invList = Array.isArray(investors) ? investors : String(investors).split(',');
        invList.forEach(inv => {
          const name = String(inv).trim();
          if (name) {
            investorCounts[name] = (investorCounts[name] || 0) + 1;
          }
        });
      }
    });
    const topInvestors = Object.entries(investorCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name: name.length > 14 ? name.slice(0, 14) + '..' : name, fullName: name, value }));

    // Deals by quarter
    const quarterCounts = {};
    dates.forEach(dateStr => {
      try {
        const date = new Date(dateStr);
        const quarter = `Q${Math.floor(date.getMonth() / 3) + 1} '${String(date.getFullYear()).slice(-2)}`;
        quarterCounts[quarter] = (quarterCounts[quarter] || 0) + 1;
      } catch (e) {}
    });
    const dealsByQuarter = Object.entries(quarterCounts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-8)
      .map(([quarter, count]) => ({ quarter, count }));

    return {
      count: deals.length,
      totalDealValue: dealSizes.reduce((a, b) => a + b, 0),
      avgDealSize: dealSizes.length > 0 ? dealSizes.reduce((a, b) => a + b, 0) / dealSizes.length : 0,
      medianDealSize: dealSizes.length > 0 ? dealSizes.sort((a, b) => a - b)[Math.floor(dealSizes.length / 2)] : 0,
      dateRange: dates.length > 0 ? `${dates.sort()[0]} to ${dates.sort()[dates.length - 1]}` : 'N/A',
      topDealTypes,
      topInvestors,
      dealsByQuarter
    };
  }, [deals]);

  // Calculate summary stats for investors
  const investorStats = useMemo(() => {
    if (investors.length === 0) return null;

    const aums = investors.map(i => parseFloat(i.aum)).filter(a => !isNaN(a) && a > 0);
    const dryPowders = investors.map(i => parseFloat(i.dry_powder)).filter(d => !isNaN(d) && d > 0);
    const investmentCounts = investors.map(i => parseInt(i.investments_count)).filter(c => !isNaN(c));

    // Investor types - truncate
    const typeCounts = {};
    investors.forEach(i => {
      let type = i.investor_type || 'Unknown';
      if (type.length > 18) type = type.slice(0, 16) + '..';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    const investorTypes = Object.entries(typeCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));

    // Top by AUM - truncate names
    const topByAUM = [...investors]
      .filter(i => parseFloat(i.aum) > 0)
      .sort((a, b) => parseFloat(b.aum) - parseFloat(a.aum))
      .slice(0, 5)
      .map(i => ({ name: i.name?.length > 12 ? i.name.slice(0, 12) + '..' : i.name, fullName: i.name, value: parseFloat(i.aum) }));

    return {
      count: investors.length,
      totalAUM: aums.reduce((a, b) => a + b, 0),
      avgAUM: aums.length > 0 ? aums.reduce((a, b) => a + b, 0) / aums.length : 0,
      totalDryPowder: dryPowders.reduce((a, b) => a + b, 0),
      avgDryPowder: dryPowders.length > 0 ? dryPowders.reduce((a, b) => a + b, 0) / dryPowders.length : 0,
      totalInvestments: investmentCounts.reduce((a, b) => a + b, 0),
      investorTypes,
      topByAUM
    };
  }, [investors]);

  // Render data table for an entity
  const renderDataTable = (entityType, data) => {
    const columns = defaultColumns[entityType];

    return (
      <div className="workbench-table-container">
        <table className="workbench-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col}>{col.replace(/_/g, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 50).map((item, idx) => (
              <tr key={item.id || item.pitchbook_id || item.pitchbook_deal_id || item.pitchbook_investor_id || idx}>
                {columns.map(column => {
                  const value = item[column];

                  // Special rendering for names
                  if (column === 'name' && item.pitchbook_id) {
                    return <td key={column} className="name-cell"><Link to={`/company/${item.pitchbook_id}`}>{value || '—'}</Link></td>;
                  }
                  if (column === 'name' && item.pitchbook_investor_id) {
                    return <td key={column} className="name-cell"><Link to={`/investor/${item.pitchbook_investor_id}`}>{value || '—'}</Link></td>;
                  }
                  if (column === 'companies' && item.pitchbook_deal_id) {
                    return <td key={column} className="name-cell"><Link to={`/deal/${item.pitchbook_deal_id}`}>{value || '—'}</Link></td>;
                  }
                  if (column === 'website') {
                    return <td key={column}>{value ? <a href={formatWebsiteUrl(value)} target="_blank" rel="noopener noreferrer">Link</a> : '—'}</td>;
                  }
                  if (column.includes('date')) {
                    return <td key={column}>{formatDate(value)}</td>;
                  }
                  if (column === 'year_founded') {
                    return <td key={column}>{formatYear(value)}</td>;
                  }
                  if (column.includes('usd') || column.includes('size') || column.includes('valuation') || column === 'aum' || column === 'dry_powder' || column === 'total_raised' || column === 'revenue') {
                    return <td key={column} className="amount-cell">{formatCurrency(value)}</td>;
                  }
                  if (Array.isArray(value)) {
                    return <td key={column}>{value.slice(0, 3).join(', ') || '—'}{value.length > 3 ? '...' : ''}</td>;
                  }

                  return <td key={column}>{value || '—'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length > 50 && (
          <div className="table-overflow-note">
            Showing 50 of {data.length} records. Export to CSV for full data.
          </div>
        )}
      </div>
    );
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{payload[0].payload.fullName || label || payload[0].name}</p>
          <p className="tooltip-value">{payload[0].value.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  // Empty state
  if (!searchExport && companies.length === 0 && deals.length === 0 && investors.length === 0) {
    return (
      <div className="data-explorer-page">
        <div className="workbench-empty-state">
          <div className="empty-state-icon">
            <FiDatabase size={64} />
          </div>
          <h2>Data Explorer Workbench</h2>
          <p>No search results loaded.</p>
          <div className="empty-state-instructions">
            <p>To use the Data Explorer:</p>
            <ol>
              <li>Search for companies, deals, or investors on the home page</li>
              <li>Click <strong>"Tabulate"</strong> to view results in table format</li>
              <li>Click <strong>"View in Data Explorer"</strong> to analyze here</li>
            </ol>
          </div>
          <button className="go-to-search-btn" onClick={() => navigate('/')}>
            <FiSearch size={18} />
            Go to Search
            <FiArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="data-explorer-page workbench-mode">
      {/* Workbench Header */}
      <div className="workbench-header">
        <div className="workbench-header-left">
          <h1>Data Explorer Workbench</h1>
          {searchExport && (
            <div className="workbench-meta">
              <span className="meta-query">Query: "{searchExport.query}"</span>
              <span className="meta-divider">|</span>
              <span className="meta-count">{searchExport.totalFound} results</span>
              <span className="meta-divider">|</span>
              <span className="meta-mode">{searchExport.searchMode}</span>
            </div>
          )}
        </div>
        <div className="workbench-header-right">
          <button className="new-search-btn" onClick={() => navigate('/')}>
            <FiSearch size={16} />
            New Search
          </button>
        </div>
      </div>

      {/* Companies Section */}
      {companies.length > 0 && (
        <div className="workbench-section">
          <div className="section-header" onClick={() => toggleSection('companies')}>
            <div className="section-title">
              <FiBriefcase size={20} />
              <h2>Companies</h2>
              <span className="record-count">{companies.length} records</span>
            </div>
            <div className="section-actions">
              <button className="export-section-btn" onClick={(e) => { e.stopPropagation(); exportToCSV('companies', companies); }}>
                <FiDownload size={16} />
                Export CSV
              </button>
              {collapsedSections.companies ? <FiChevronDown size={20} /> : <FiChevronUp size={20} />}
            </div>
          </div>

          {!collapsedSections.companies && (
            <div className="section-content">
              {/* Data Table */}
              {renderDataTable('companies', companies)}

              {/* Summary Stats with Database Comparison */}
              {companyStats && (
                <div className="summary-stats">
                  <h3><FiBarChart2 size={18} /> Summary Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon blue"><FiUsers size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Avg Employees</span>
                        <span className="stat-value">{companyStats.avgEmployees.toLocaleString()}</span>
                        <span className="stat-sub">Median: {companyStats.medianEmployees.toLocaleString()}</span>
                        {dbStats?.avg_employees && (
                          <span className="stat-db-compare">DB Avg: {Math.round(dbStats.avg_employees).toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon green"><FiDollarSign size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Avg Total Raised</span>
                        <span className="stat-value">{formatCurrency(companyStats.avgTotalRaised)}</span>
                        <span className="stat-sub">Sum: {formatCurrency(companyStats.totalRaisedSum)}</span>
                        {dbStats?.avg_total_raised && (
                          <span className="stat-db-compare">DB Avg: {formatCurrency(dbStats.avg_total_raised)}</span>
                        )}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon purple"><FiCalendar size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Founded Range</span>
                        <span className="stat-value">{companyStats.yearRange}</span>
                        <span className="stat-sub">{companies.length} companies</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon orange"><FiMapPin size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Top Location</span>
                        <span className="stat-value">{companyStats.topLocations[0]?.name || 'N/A'}</span>
                        <span className="stat-sub">{companyStats.topLocations[0]?.value || 0} companies</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Visualizations */}
              {companyStats && (
                <div className="section-charts">
                  <h3><FiTrendingUp size={18} /> Visualizations</h3>
                  <div className="charts-grid">
                    {/* Top Industries Bar Chart */}
                    <div className="chart-card">
                      <h4>Top Industries</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={companyStats.topIndustries} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Financing Status Pie Chart - No external labels */}
                    <div className="chart-card">
                      <h4>Financing Status</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={companyStats.financingStatusData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="45%"
                            outerRadius={60}
                            innerRadius={25}
                            labelLine={false}
                            label={renderCustomizedLabel}
                          >
                            {companyStats.financingStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Top Locations Bar Chart */}
                    <div className="chart-card">
                      <h4>Top Locations</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={companyStats.topLocations} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Founded by Year */}
                    <div className="chart-card">
                      <h4>Companies Founded by Year</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={companyStats.foundedByYear} margin={{ left: 0, right: 10 }}>
                          <XAxis dataKey="year" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Deals Section */}
      {deals.length > 0 && (
        <div className="workbench-section">
          <div className="section-header" onClick={() => toggleSection('deals')}>
            <div className="section-title">
              <FiDollarSign size={20} />
              <h2>Deals</h2>
              <span className="record-count">{deals.length} records</span>
            </div>
            <div className="section-actions">
              <button className="export-section-btn" onClick={(e) => { e.stopPropagation(); exportToCSV('deals', deals); }}>
                <FiDownload size={16} />
                Export CSV
              </button>
              {collapsedSections.deals ? <FiChevronDown size={20} /> : <FiChevronUp size={20} />}
            </div>
          </div>

          {!collapsedSections.deals && (
            <div className="section-content">
              {/* Data Table */}
              {renderDataTable('deals', deals)}

              {/* Summary Stats with Database Comparison */}
              {dealStats && (
                <div className="summary-stats">
                  <h3><FiBarChart2 size={18} /> Summary Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon green"><FiDollarSign size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Avg Deal Size</span>
                        <span className="stat-value">{formatCurrency(dealStats.avgDealSize)}</span>
                        <span className="stat-sub">Total: {formatCurrency(dealStats.totalDealValue)}</span>
                        {dbStats?.avg_deal_size && (
                          <span className="stat-db-compare">DB Avg: {formatCurrency(dbStats.avg_deal_size)}</span>
                        )}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon blue"><FiTrendingUp size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Median Deal Size</span>
                        <span className="stat-value">{formatCurrency(dealStats.medianDealSize)}</span>
                        <span className="stat-sub">{deals.length} deals</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon purple"><FiCalendar size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Date Range</span>
                        <span className="stat-value-small">{dealStats.dateRange}</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon orange"><FiBriefcase size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Top Deal Type</span>
                        <span className="stat-value">{dealStats.topDealTypes[0]?.fullName || 'N/A'}</span>
                        <span className="stat-sub">{dealStats.topDealTypes[0]?.value || 0} deals</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Visualizations */}
              {dealStats && (
                <div className="section-charts">
                  <h3><FiTrendingUp size={18} /> Visualizations</h3>
                  <div className="charts-grid">
                    {/* Deal Types Distribution */}
                    <div className="chart-card">
                      <h4>Deal Types Distribution</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dealStats.topDealTypes} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 10 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Deal Volume by Quarter */}
                    <div className="chart-card">
                      <h4>Deal Volume by Quarter</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={dealStats.dealsByQuarter} margin={{ left: 0, right: 20 }}>
                          <XAxis dataKey="quarter" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Top Lead Investors */}
                    {dealStats.topInvestors.length > 0 && (
                      <div className="chart-card chart-card-wide">
                        <h4>Top Lead Investors</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={dealStats.topInvestors} margin={{ left: 10, right: 20, bottom: 20 }}>
                            <XAxis dataKey="name" tick={{ fontSize: 10, angle: -20, textAnchor: 'end' }} height={50} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Investors Section */}
      {investors.length > 0 && (
        <div className="workbench-section">
          <div className="section-header" onClick={() => toggleSection('investors')}>
            <div className="section-title">
              <FiUsers size={20} />
              <h2>Investors</h2>
              <span className="record-count">{investors.length} records</span>
            </div>
            <div className="section-actions">
              <button className="export-section-btn" onClick={(e) => { e.stopPropagation(); exportToCSV('investors', investors); }}>
                <FiDownload size={16} />
                Export CSV
              </button>
              {collapsedSections.investors ? <FiChevronDown size={20} /> : <FiChevronUp size={20} />}
            </div>
          </div>

          {!collapsedSections.investors && (
            <div className="section-content">
              {/* Data Table */}
              {renderDataTable('investors', investors)}

              {/* Summary Stats with Database Comparison */}
              {investorStats && (
                <div className="summary-stats">
                  <h3><FiBarChart2 size={18} /> Summary Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon green"><FiDollarSign size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Avg AUM</span>
                        <span className="stat-value">{formatCurrency(investorStats.avgAUM)}</span>
                        <span className="stat-sub">Total: {formatCurrency(investorStats.totalAUM)}</span>
                        {dbStats?.avg_aum && (
                          <span className="stat-db-compare">DB Avg: {formatCurrency(dbStats.avg_aum)}</span>
                        )}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon blue"><FiTrendingUp size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Avg Dry Powder</span>
                        <span className="stat-value">{formatCurrency(investorStats.avgDryPowder)}</span>
                        <span className="stat-sub">Total: {formatCurrency(investorStats.totalDryPowder)}</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon purple"><FiBriefcase size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Total Investments</span>
                        <span className="stat-value">{investorStats.totalInvestments.toLocaleString()}</span>
                        <span className="stat-sub">{investors.length} investors</span>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-icon orange"><FiUsers size={20} /></div>
                      <div className="stat-content">
                        <span className="stat-label">Top Type</span>
                        <span className="stat-value">{investorStats.investorTypes[0]?.name || 'N/A'}</span>
                        <span className="stat-sub">{investorStats.investorTypes[0]?.value || 0} investors</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Visualizations */}
              {investorStats && (
                <div className="section-charts">
                  <h3><FiTrendingUp size={18} /> Visualizations</h3>
                  <div className="charts-grid">
                    {/* Investor Type Pie - No external labels */}
                    <div className="chart-card">
                      <h4>Investor Types</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={investorStats.investorTypes}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="45%"
                            outerRadius={60}
                            innerRadius={25}
                            labelLine={false}
                            label={renderCustomizedLabel}
                          >
                            {investorStats.investorTypes.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Top by AUM */}
                    {investorStats.topByAUM.length > 0 && (
                      <div className="chart-card chart-card-wide">
                        <h4>Top Investors by AUM</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={investorStats.topByAUM} margin={{ left: 10, right: 20, bottom: 20 }}>
                            <XAxis dataKey="name" tick={{ fontSize: 10, angle: -20, textAnchor: 'end' }} height={50} />
                            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}B`} tick={{ fontSize: 10 }} />
                            <Tooltip content={<CustomTooltip />} formatter={(v) => formatCurrency(v)} />
                            <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DataExplorer;
